import React, { type MouseEvent, useCallback } from 'react';

import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { useTab } from '@atlaskit/tabs';
import { IconButton } from '@atlaskit/button/new';
import RetryIcon from '@atlaskit/icon/core/retry';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ConditionalTooltip } from '@atlassian/jira-jql-builder-common/src/ui/conditional-tooltip/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';
import { useLabelOverflow } from './hooks.tsx';

type Props = {
	label: string;
	maxWidth?: string;
	onRestoreDefaults?: (() => void) | boolean;
};

const Tab = ({ label, maxWidth = '100%', onRestoreDefaults }: Props) => {
	const { formatMessage } = useIntl();
	const { labelOverflows, onLabelRef } = useLabelOverflow();
	const tabAttributes = useTab();

	const handleRestoreDefaults = useCallback(
		(mouseEvent: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'filterSearchRestoreDefaults', {
				// If number of clicks is 0, button was actioned using the keyboard
				keyboard: mouseEvent.detail === 0,
			});

			onRestoreDefaults && typeof onRestoreDefaults === 'function' && onRestoreDefaults();
		},
		[onRestoreDefaults],
	);

	if (fg('jira_inline_field_config_gate')) {
		return (
			<TabStyled maxWidthValue={maxWidth} {...tabAttributes}>
				<ConditionalTooltip condition={labelOverflows} content={label} position="top">
					<Box xcss={tabStyles}>
						<Box ref={onLabelRef} xcss={textStyles}>
							{label}
						</Box>
						{onRestoreDefaults !== undefined &&
							typeof onRestoreDefaults === 'function' &&
							tabAttributes['aria-selected'] && (
								<IconButton
									icon={RetryIcon}
									onClick={handleRestoreDefaults}
									label={formatMessage(messages.restoreDefaults)}
									appearance="subtle"
									spacing="compact"
									isTooltipDisabled={false}
								/>
							)}
					</Box>
				</ConditionalTooltip>
			</TabStyled>
		);
	}
	return (
		<TabStyled maxWidthValue={maxWidth} {...tabAttributes}>
			<ConditionalTooltip condition={labelOverflows} content={label} position="top">
				<Box ref={onLabelRef} xcss={textStyles}>
					{label}
				</Box>
			</ConditionalTooltip>
		</TabStyled>
	);
};

const textStyles = xcss({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
});

const tabStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabStyled = styled.div<{
	maxWidthValue: string;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: ({ maxWidthValue }) => maxWidthValue,
	boxSizing: 'border-box',
	alignContent: 'center',
});

export default Tab;
